import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { formatError } from "@/lib/api";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import PoweredBy from "@/components/PoweredBy";
import { KeyRound, ArrowRight, Eye, EyeOff } from "lucide-react";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password reset successfully");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-dvh bg-[#0B1429]">
      <Navbar />
      <div className="pt-28 pb-16 px-4 sm:px-6 max-w-md mx-auto">
        <div className="card-dark p-8 md:p-10">
          <div className="flex items-center gap-2 mb-6">
            <KeyRound className="w-5 h-5 text-[#00F0FF]" />
            <h2 className="font-display font-bold text-2xl">Reset Password</h2>
          </div>

          {!token ? (
            <div className="text-sm text-[#94A3B8]" data-testid="reset-no-token">
              This link is missing a reset token. Request a new one from the{" "}
              <Link to="/login" className="text-[#00F0FF] hover:underline">login page</Link>.
            </div>
          ) : done ? (
            <div className="space-y-4" data-testid="reset-success">
              <p className="text-sm text-[#94A3B8]">
                Your password has been reset. You can now sign in with your new password.
              </p>
              <button
                onClick={() => navigate("/login", { replace: true })}
                className="btn-primary w-full justify-center min-h-[44px] touch-manipulation"
                data-testid="reset-go-to-login"
              >
                Go to Login <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4" data-testid="reset-password-form">
              <div>
                <label htmlFor="reset-password" className="text-xs uppercase tracking-wider text-[#94A3B8] mb-1.5 block">
                  New Password
                </label>
                <div className="relative">
                  <input
                    id="reset-password"
                    data-testid="reset-password-input"
                    type={showPwd ? "text" : "password"}
                    required
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-dark pr-12"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd(!showPwd)}
                    aria-label={showPwd ? "Hide password" : "Show password"}
                    className="absolute right-0 top-0 h-full w-11 flex items-center justify-center text-[#94A3B8] hover:text-[#00F0FF] touch-manipulation"
                    data-testid="toggle-reset-password"
                  >
                    {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <label htmlFor="reset-confirm-password" className="text-xs uppercase tracking-wider text-[#94A3B8] mb-1.5 block">
                  Confirm New Password
                </label>
                <input
                  id="reset-confirm-password"
                  data-testid="reset-confirm-password-input"
                  type={showPwd ? "text" : "password"}
                  required
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="input-dark"
                  placeholder="••••••••"
                />
              </div>
              <button
                data-testid="reset-submit"
                type="submit"
                disabled={loading}
                className="btn-primary w-full justify-center mt-2 min-h-[44px] touch-manipulation"
              >
                {loading ? "Resetting..." : "Reset Password"} <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          )}

          <div className="mt-6 pt-5 border-t border-white/5 flex justify-center">
            <PoweredBy />
          </div>
        </div>
      </div>
    </div>
  );
}
